/**
 * Client-side derived columns: formulas on values from Orion leaf calcs (same row),
 * resolved via the same calcKey + CALC_SOURCES.fields pipeline as material columns.
 *
 * Formulas are documented in DEVELOPMENT_NOTES — confirm with stakeholders before
 * treating labels (DVPI, MOIC) as authoritative business definitions.
 */

import { CALC_SOURCES, COLUMN_CATALOG_MAP } from './constants'
import { resolveColumnOptions, toNumber } from './calcResolve'

/**
 * Registry keyed by catalog `deriveId`.
 * calcInputs: semantic inputs; each row maps to one calcKey via source + optionTemplate.
 */
export const DERIVE_REGISTRY = {
  dvpi: {
    description: 'Distributions to paid-in: (cash + recallable) / contributed',
    calcInputs: [
      { key: 'contributed', source: 'aip', field: 'contributedCapital', optionTemplate: {}, representativeColumnType: 'aip:contributedCapital' },
      { key: 'cashDist', source: 'aip', field: 'cashDist', optionTemplate: {}, representativeColumnType: 'aip:cashDist' },
      { key: 'recallDist', source: 'aip', field: 'recallDist', optionTemplate: {}, representativeColumnType: 'aip:recallDist' }
    ],
    compute: (vals) => {
      const denom = toNumber(vals.contributed)
      if (denom == null || denom === 0) return null
      const cash = toNumber(vals.cashDist) ?? 0
      const recall = toNumber(vals.recallDist) ?? 0
      return (cash + recall) / denom
    }
  },
  moic: {
    description: 'Total value / paid-in (TVPI-style): (market value + cash + recallable) / contributed',
    calcInputs: [
      { key: 'marketValue', source: 'marketValue', field: 'primary', optionTemplate: {}, representativeColumnType: 'marketValue' },
      { key: 'contributed', source: 'aip', field: 'contributedCapital', optionTemplate: {}, representativeColumnType: 'aip:contributedCapital' },
      { key: 'cashDist', source: 'aip', field: 'cashDist', optionTemplate: {}, representativeColumnType: 'aip:cashDist' },
      { key: 'recallDist', source: 'aip', field: 'recallDist', optionTemplate: {}, representativeColumnType: 'aip:recallDist' }
    ],
    compute: (vals) => {
      const denom = toNumber(vals.contributed)
      if (denom == null || denom === 0) return null
      const mv = toNumber(vals.marketValue) ?? 0
      const cash = toNumber(vals.cashDist) ?? 0
      const recall = toNumber(vals.recallDist) ?? 0
      return (mv + cash + recall) / denom
    }
  }
}

/**
 * Pre-resolve merge: defaultOptions + registry input.optionTemplate + column.options.dependencyOptions[source].
 * Used for synthetic column `options` and must match resolveCalcAssignments + resolveColumnOptions.
 */
export function getMergedDependencyOpts (input, columnOptions) {
  const source = CALC_SOURCES[input.source]
  if (!source) return null
  return {
    ...source.defaultOptions,
    ...input.optionTemplate,
    ...(columnOptions?.dependencyOptions?.[input.source] || {})
  }
}

/** Resolved column options for calcKey identity (global date fallback applied). */
export function mergeDependencyInputOptions (input, columnOptions, globalDateConfig) {
  const mergedOpts = getMergedDependencyOpts(input, columnOptions)
  if (!mergedOpts) return null
  return resolveColumnOptions(mergedOpts, globalDateConfig)
}

function resolveInputValue (input, columnOptions, calcAssignments, leafCalcsById, globalDateConfig) {
  const source = CALC_SOURCES[input.source]
  if (!source) return null
  const resolved = mergeDependencyInputOptions(input, columnOptions, globalDateConfig)
  if (!resolved) return null
  const key = source.calcKey(resolved)
  const group = calcAssignments.get(key)
  if (!group) return null
  const calc = leafCalcsById.get(group.calcId)
  if (!calc) return null
  const fieldDef = source.fields[input.field]
  if (!fieldDef) return null
  return fieldDef.extract(calc)
}

/**
 * Mutates `row` to add values for derived column instance ids.
 * Reads inputs from leaf Orion calcs via assignments (same calcKey path as the request).
 */
export function applyDerivedColumns (row, columnInstances, catalogMap, calcAssignments, leafCalcs, globalDateConfig) {
  if (!row || !columnInstances?.length) return

  const leafCalcsById = new Map(
    (leafCalcs || []).filter(c => c?.id).map(c => [c.id, c])
  )

  for (const col of columnInstances) {
    const catalog = catalogMap[col.columnType]
    if (!catalog || catalog.kind !== 'derived') continue
    const deriveId = catalog.deriveId
    const def = DERIVE_REGISTRY[deriveId]
    if (!def?.calcInputs) continue

    const vals = {}
    for (const input of def.calcInputs) {
      vals[input.key] = resolveInputValue(input, col.options, calcAssignments, leafCalcsById, globalDateConfig)
    }

    row[col.id] = def.compute(vals)
  }
}

export function isDerivedColumnType (columnType) {
  return COLUMN_CATALOG_MAP[columnType]?.kind === 'derived'
}
